// src/utils/services/hccAiService.js
import { callOpenAIChat } from './openaiClient';
import phiSanitizer from '../security/phiSanitizer';

// 🔧 Helper: extrae el JSON aunque venga con ```json ... ``` o texto extra
const safeParseJsonFromModel = (rawContent) => {
  if (!rawContent) {
    throw new Error('No content to parse');
  }

  // Si por alguna razón viene como array de partes, las unimos
  let text = Array.isArray(rawContent)
    ? rawContent
        .map((p) =>
          typeof p === 'string'
            ? p
            : (p && (p.text || p.content || '')) || ''
        )
        .join('')
    : String(rawContent);

  text = text.trim();

  // 1) Si viene con ```json ... ``` o ``` ... ``` lo limpiamos
  if (text.startsWith('```')) {
    // Quitamos la primera línea ```json o ``` y la última ```
    text = text.replace(/^```[a-zA-Z]*\s*/m, '');
    text = text.replace(/```$/m, '');
    text = text.trim();
  }

  // 2) Si aún hay texto alrededor, recortamos desde el primer '{' al último '}'
  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');

  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    console.error('[safeParseJsonFromModel] No JSON object found in text:', text);
    throw new Error('No JSON object found in model response');
  }

  const jsonSlice = text.slice(firstBrace, lastBrace + 1);

  // (Opcional: log en dev)
  // console.log('[safeParseJsonFromModel] JSON slice to parse:', jsonSlice);

  return JSON.parse(jsonSlice);
};


/**
 * 🔹 Nuevo servicio IA:
 * A partir de TODAS las notas clínicas arma:
 *  - currentConditions: HCC claramente soportadas (RAF actual)
 *  - futureConditions: HCC adicionales sugeridas / posibles
 *
 * Ambos arrays usan el MISMO esquema de los demás componentes:
 * {
 *   hcc: "HCC 18",
 *   icd10: "E11.9",
 *   description: "...",
 *   rafWeight: 0.32,
 *   meat: { monitoring: {...}, treatment: {...} }
 * }
 */
export const generateHccRafModelFromAI = async ({ clinicalNotes }) => {
  const rawNotesText = Array.isArray(clinicalNotes)
    ? clinicalNotes.join('\n---\n')
    : clinicalNotes || '';

  // HIPAA 45 CFR 164.514: desensibilizar PHI (SSN, teléfonos, emails, DOB,
  // direcciones, MRN, ZIP) de las notas antes de enviarlas al proxy de IA
  const notesText = phiSanitizer.sanitizeText(rawNotesText);

  if (!notesText || notesText.trim().length === 0) {
    console.warn('[generateHccRafModelFromAI] No clinical notes text');
    return {
      currentConditions: [],
      futureConditions: [],
      analysisSummary: '',
    };
  }

  const userPrompt = `
You are a physician specialized in HCC (Hierarchical Condition Category) risk adjustment coding.

I will provide you with COMPLETE CLINICAL NOTES for a single patient
(Annual Wellness 2.0, Follow Up Exam 2.0, Telehealth 2.0, Hospital Discharge Follow Up, etc).

Your task is to build TWO HCC lists:

1) "currentConditions":
   - Conditions that are clearly ACTIVE and WELL SUPPORTED by MEAT documentation
     in the notes (Monitoring, Evaluation/Assessment, and/or Treatment).
   - These represent the most reliable HCCs to use for the CURRENT RAF SCORE.

2) "futureConditions":
   - All conditions from "currentConditions", PLUS any additional HCCs that:
       - appear as possible / probable,
       - or show incomplete documentation,
       - or would reasonably be captured if documentation is improved,
       - or are chronic conditions that the patient logically has
         but the documentation is weak in some notes.
   - These represent a PROJECTED RAF SCORE if all suggested codes are
     properly documented and captured.

Return ONLY a JSON object with this structure:

{
  "currentConditions": [
    {
      "hcc": "HCC 18",
      "icd10": "E11.9",
      "description": "Short description in English",
      "rafWeight": 0.32,
      "meat": {
        "monitoring": {
          "date": "YYYY-MM-DD or 'N/A'",
          "source": "Short note snippet or section name",
          "noteType": "Annual Wellness, Follow Up Exam, Telehealth, etc.",
          "provider": "Provider type or generic name"
        },
        "treatment": {
          "medication": "Main medication or treatment, or 'N/A'",
          "date": "YYYY-MM-DD or 'N/A'",
          "provider": "Provider/service for the treatment"
        }
      }
    }
  ],
  "futureConditions": [
    {
      "hcc": "HCC 18",
      "icd10": "E11.9",
      "description": "Short description in English",
      "rafWeight": 0.32,
      "meat": {
        "monitoring": {...},
        "treatment": {...}
      }
    }
  ],
  "analysisSummary": "1–3 short sentences in English explaining key HCC gaps."
}

Important rules:
- DO NOT write anything outside of the JSON.
- "futureConditions" MUST contain at least all codes in "currentConditions".
- If you are unsure about exact RAF values, use reasonable approximations (e.g. 0.18, 0.32, 0.40).
- Focus on chronic conditions relevant to HCC (diabetes, hypertension, CKD, COPD, CHF, obesity, depression, etc.).
- If evidence is weak, mark it in the description as "possible" or "probable", but still include a rafWeight.
- Never leave "rafWeight" empty.

===== MERGED PATIENT CLINICAL NOTES =====

${notesText}
`;

  const data = await callOpenAIChat({
    model: 'gpt-4o-mini',
    messages: [{ role: 'user', content: userPrompt }],
    temperature: 0.2,
  });

  const content = data?.choices?.[0]?.message?.content;
  // HIPAA: no loguear el contenido de la respuesta (análisis clínico del paciente)

  if (!content) {
    console.error('[generateHccRafModelFromAI] Empty response from OpenAI');
    throw new Error('Empty response from OpenAI');
  }

  try {
    const json = safeParseJsonFromModel(content);

    // Soportar formato antiguo { conditions: [] } como fallback
    if (!json.currentConditions && Array.isArray(json.conditions)) {
      json.currentConditions = json.conditions;
    }

    const currentConditions = Array.isArray(json.currentConditions)
      ? json.currentConditions
      : [];

    const futureConditions = Array.isArray(json.futureConditions) &&
      json.futureConditions.length > 0
        ? json.futureConditions
        : currentConditions;

    return {
      currentConditions,
      futureConditions,
      analysisSummary: json.analysisSummary || '',
    };
  } catch (err) {
    console.error('[generateHccRafModelFromAI] Error parsing JSON:', err);
    return {
      currentConditions: [],
      futureConditions: [],
      analysisSummary: '',
    };
  }
};

export const generateHccConditionsFromAI = async ({ clinicalNotes }) => {
  const rawNotesText = Array.isArray(clinicalNotes)
    ? clinicalNotes.join('\n---\n')
    : clinicalNotes || '';

  // HIPAA 45 CFR 164.514: desensibilizar PHI antes de enviar al proxy de IA
  const notesText = phiSanitizer.sanitizeText(rawNotesText);

  if (!notesText || notesText.trim().length === 0) {
    console.warn('[generateHccConditionsFromAI] No clinical notes text');
    return [];
  }

  const userPrompt = `
You are a physician specialized in HCC (Hierarchical Condition Category) risk adjustment coding.

I will provide you with a set of COMPLETE CLINICAL NOTES of a single patient,
coming from several encounter types (Annual Wellness 2.0, Follow Up Exam 2.0, Telehealth 2.0, Hospital Discharge Follow Up).

Your task is:
1. Identify the MAIN chronic conditions relevant to HCC.
2. Return ONLY a JSON object with the following structure (no extra text):

{
  "conditions": [
    {
      "hcc": "HCC code or category (e.g. HCC 18)",
      "icd10": "Primary ICD-10 code (e.g. E11.9)",
      "description": "Short clinical description of the condition in English",
      "rafWeight": 0.00,
      "meat": {
        "monitoring": {
          "date": "YYYY-MM-DD or 'N/A'",
          "source": "Short summary of the note section used as evidence",
          "noteType": "Note type (Annual Wellness, Follow Up Exam, Telehealth, Hospital FU, etc.)",
          "provider": "Provider type or generic provider name if not specified"
        },
        "treatment": {
          "medication": "Main medication or treatment if mentioned, or 'N/A'",
          "date": "YYYY-MM-DD or 'N/A'",
          "provider": "Provider or service indicating the treatment"
        }
      }
    }
  ]
}

Important rules:
- DO NOT write anything outside of the JSON.
- Return from 1 to 10 conditions depending on the available information.
- If you are not sure about the exact RAF value, use common approximate values (e.g. 0.18, 0.32, 0.40). Never leave rafWeight empty.
- Prioritize common chronic conditions (diabetes, hypertension, CKD, COPD, CHF, etc.) when the text suggests them.
- If evidence is weak, clearly mark the description as "possible" or "probable".

===== PATIENT CLINICAL NOTES (MERGED) =====

${notesText}
`;

  const data = await callOpenAIChat({
    model: 'gpt-4o-mini',
    messages: [{ role: 'user', content: userPrompt }],
    temperature: 0.2,
  });

  const content = data?.choices?.[0]?.message?.content;
  // HIPAA: no loguear el contenido de la respuesta (análisis clínico del paciente)

  if (!content) {
    console.error('[generateHccConditionsFromAI] Empty response from OpenAI');
    throw new Error('Empty response from OpenAI');
  }

  try {
    const json = safeParseJsonFromModel(content);

    if (!json.conditions || !Array.isArray(json.conditions)) {
      console.error(
        '[generateHccConditionsFromAI] Unexpected JSON format:',
        json
      );
      throw new Error('Unexpected JSON format (missing "conditions")');
    }

    return json.conditions;
  } catch (err) {
    console.error('[generateHccConditionsFromAI] Error parsing JSON:', err);
    return [];
  }
};
